import React, { useEffect, useMemo, useState } from "react";
import api from "../../services/fixedIncomeApi";
import usersApi from "../../services/api";

const PRODUCT_TYPES = ["BOND", "CP", "PLACEMENT", "TBILL"];
const NOTE_SUGGESTIONS = {
  BOND: "Bond Investment",
  CP: "Commercial Paper",
  PLACEMENT: "Money Market Placement",
  TBILL: "Treasury Bill",
};
const STRUCTURES = [
  { value: "monthly_interest_maturity_principal", label: "Monthly interest + principal at maturity" },
  { value: "quarterly_interest_maturity_principal", label: "Quarterly interest + principal at maturity" },
  { value: "biannual_interest_maturity_principal", label: "Bi-annual interest + principal at maturity" },
  { value: "bullet", label: "Bullet (principal + interest at maturity)" },
  { value: "equal_installment_monthly", label: "Equal installment (monthly)" },
  { value: "equal_installment_quarterly", label: "Equal installment (quarterly)" },
  { value: "equal_installment_biannual", label: "Equal installment (bi-annual)" },
];

const FixedIncomeWizard = () => {
  const [step, setStep] = useState(1);
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [schedule, setSchedule] = useState([]);
  const [summary, setSummary] = useState(null);
  const [opportunities, setOpportunities] = useState([]);
  const [requests, setRequests] = useState([]);
  const [submittedRequestIds, setSubmittedRequestIds] = useState([]);
  const [form, setForm] = useState({
    user_id: "",
    user_ids: [],
    bulk: false,
    product_type: "BOND",
    note: NOTE_SUGGESTIONS.BOND,
    principal: "",
    rate: "",
    discount_rate: "",
    tenor_days: "",
    start_date: "",
    maturity_date: "",
    payment_frequency: "annual",
    structure_type: STRUCTURES[0].value,
    interest_method: "simple", // simple | compound
  });

  useEffect(() => {
    const loadUsers = async () => {
      try {
        setLoadingUsers(true);
        const data = await usersApi.adminListFinanceClients();
        setUsers(Array.isArray(data) ? data : []);
      } catch (err) {
        setUsers([]);
        setError(err.message || "Unable to load clients");
      } finally {
        setLoadingUsers(false);
      }
    };
    loadUsers();
  }, []);

  useEffect(() => {
    let cancelled = false;
    const loadRequests = async () => {
      try {
        const data = await api.listFixedIncomeRequests();
        if (!cancelled) setRequests(Array.isArray(data) ? data : []);
      } catch (err) {
        if (!cancelled) setError(err.message || "Unable to load fixed-income requests");
      }
    };
    loadRequests();
    const interval = setInterval(loadRequests, 10000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, []);

  useEffect(() => {
    const loadOpps = async () => {
      try {
        const data = await api.listFixedIncomeOpportunities();
        setOpportunities(Array.isArray(data) ? data : []);
      } catch {
        setOpportunities([]);
      }
    };
    loadOpps();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "product_type") {
      setForm((prev) => ({
        ...prev,
        product_type: value,
        note:
          !prev.note || prev.note === NOTE_SUGGESTIONS[prev.product_type]
            ? NOTE_SUGGESTIONS[value] || prev.note
            : prev.note,
      }));
      return;
    }
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const toggleBulk = (checked) => {
    setForm((prev) => ({
      ...prev,
      bulk: checked,
      user_id: checked ? "" : prev.user_id,
      user_ids: checked ? prev.user_ids : [],
    }));
  };

  const applyOpportunity = (opp) => {
    setForm((prev) => ({
      ...prev,
      product_type: opp.product_type || prev.product_type,
      principal: opp.principal || opp.min_amount || prev.principal,
      rate: opp.rate || prev.rate,
      tenor_days: opp.tenor_days || prev.tenor_days,
      payment_frequency: opp.payment_frequency || prev.payment_frequency,
      structure_type: opp.structure_type || prev.structure_type,
      note:
        !prev.note || prev.note === NOTE_SUGGESTIONS[prev.product_type]
          ? NOTE_SUGGESTIONS[opp.product_type || prev.product_type] || prev.note
          : prev.note,
      interest_method: prev.interest_method || "simple",
    }));
  };

  const handleSimulate = async () => {
    setError("");
    setSuccess("");
    setLoading(true);
    try {
      const payload = { ...form };
      const res = await api.simulateFixedIncome(payload);
      setSchedule(res.schedule || []);
      setSummary(res.summary || null);
      setStep(3);
    } catch (err) {
      setError(err.message || "Simulation failed");
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    setError("");
    setSuccess("");
    setLoading(true);
    try {
      const payload = { ...form, schedule };
      let res;
      if (form.bulk) {
        payload.user_ids = form.user_ids;
        res = await api.bulkCreateFixedIncome(payload);
        const createdCount = (res?.created || []).length;
        const failedCount = (res?.failed || []).length;
        setSubmittedRequestIds((res?.created || []).map((item) => item?.investment?.id));
        setSuccess(
          `${createdCount} withdrawal request(s) submitted to Accounts; ${failedCount} failed.`
        );
      } else {
        res = await api.createFixedIncome(payload);
        setSubmittedRequestIds(res?.investment?.id ? [res.investment.id] : []);
        setSuccess(
          `Withdrawal request #${res?.pending_transaction_id || ""} submitted to Accounts.`
        );
      }
      const latest = await api.listFixedIncomeRequests();
      setRequests(Array.isArray(latest) ? latest : []);
      setStep(4);
    } catch (err) {
      setError(err.message || "Creation failed");
    } finally {
      setLoading(false);
    }
  };

  const renderStep1 = () => (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold">Select User(s)</h3>
        <label className="flex items-center gap-2 text-sm text-slate-600">
          <input
            type="checkbox"
            checked={form.bulk}
            onChange={(e) => toggleBulk(e.target.checked)}
          />
          Bulk create
        </label>
      </div>

      {form.bulk ? (
        <div className="space-y-2">
          <p className="text-xs text-slate-600">Choose one or more approved users.</p>
          <select
            multiple
            value={form.user_ids}
            onChange={(e) => {
              const vals = Array.from(e.target.selectedOptions || []).map((o) => o.value);
              setForm((prev) => ({ ...prev, user_ids: vals }));
            }}
            className="w-full rounded border border-slate-200 px-3 py-2 h-48"
          >
            {loadingUsers ? (
              <option>Loading...</option>
            ) : (
              users.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.full_name || u.username || u.email} ({u.id})
                </option>
              ))
            )}
          </select>
        </div>
      ) : (
        <select
          name="user_id"
          value={form.user_id}
          onChange={handleChange}
          className="w-full rounded border border-slate-200 px-3 py-2"
        >
          <option value="">{loadingUsers ? "Loading..." : "Select user"}</option>
          {users.map((u) => (
            <option key={u.id} value={u.id}>
              {u.full_name || u.username}
            </option>
          ))}
        </select>
      )}
      <div className="flex gap-2">
        <button
          type="button"
          onClick={() => setStep(2)}
          disabled={form.bulk ? form.user_ids.length === 0 : !form.user_id}
          className="rounded bg-emerald-600 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
        >
          Next
        </button>
      </div>
    </div>
  );

  const renderStep2 = () => (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold">Define Investment</h3>
        <select
          onChange={(e) => {
            const opp = opportunities.find((o) => String(o.id) === e.target.value);
            if (opp) applyOpportunity(opp);
          }}
          className="rounded border border-slate-200 px-3 py-2 text-sm"
        >
          <option value="">Select opportunity (optional)</option>
          {opportunities.map((opp) => (
            <option key={opp.id} value={opp.id}>
              {opp.name || opp.product_type} - {opp.rate}% - {opp.tenor_days}d
            </option>
          ))}
        </select>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <div>
          <label className="text-sm font-medium text-slate-700">Product</label>
          <select
            name="product_type"
            value={form.product_type}
            onChange={handleChange}
            className="mt-1 w-full rounded border border-slate-200 px-3 py-2"
          >
            {PRODUCT_TYPES.map((p) => (
              <option key={p} value={p}>
                {p}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="text-sm font-medium text-slate-700">
            Transaction note (shown to user)
          </label>
          <input
            name="note"
            type="text"
            value={form.note}
            onChange={handleChange}
            placeholder="Money Market Placement"
            className="mt-1 w-full rounded border border-slate-200 px-3 py-2"
          />
          <p className="mt-1 text-xs text-slate-500">
            This note appears in the user transaction history.
          </p>
        </div>
        <div>
          <label className="text-sm font-medium text-slate-700">Principal / Face value</label>
          <input
            name="principal"
            type="number"
            value={form.principal}
            onChange={handleChange}
            className="mt-1 w-full rounded border border-slate-200 px-3 py-2"
          />
        </div>
        <div>
          <label className="text-sm font-medium text-slate-700">Rate (%)</label>
          <input
            name="rate"
            type="number"
            value={form.rate}
            onChange={handleChange}
            className="mt-1 w-full rounded border border-slate-200 px-3 py-2"
          />
        </div>
        <div>
          <label className="text-sm font-medium text-slate-700">Tenor (days)</label>
          <input
            name="tenor_days"
            type="number"
            value={form.tenor_days}
            onChange={handleChange}
            className="mt-1 w-full rounded border border-slate-200 px-3 py-2"
          />
        </div>
        <div>
          <label className="text-sm font-medium text-slate-700">Start date</label>
          <input
            name="start_date"
            type="date"
            value={form.start_date}
            onChange={handleChange}
            className="mt-1 w-full rounded border border-slate-200 px-3 py-2"
          />
        </div>
        {form.product_type === "BOND" || form.product_type === "PLACEMENT" ? (
          <>
            <div>
              <label className="text-sm font-medium text-slate-700">Payment frequency</label>
              <select
                name="payment_frequency"
                value={form.payment_frequency}
                onChange={handleChange}
                className="mt-1 w-full rounded border border-slate-200 px-3 py-2"
              >
                <option value="monthly">Monthly</option>
                <option value="quarterly">Quarterly</option>
                <option value="bi-annual">Bi-Annual</option>
                <option value="annual">Annual</option>
                <option value="bullet">Bullet</option>
              </select>
            </div>
            {form.product_type === "PLACEMENT" && (
              <div>
                <label className="text-sm font-medium text-slate-700">Structure</label>
                <select
                  name="structure_type"
                  value={form.structure_type}
                  onChange={handleChange}
                  className="mt-1 w-full rounded border border-slate-200 px-3 py-2"
                >
                  {STRUCTURES.map((s) => (
                    <option key={s.value} value={s.value}>
                      {s.label}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </>
        ) : null}
        <div>
          <label className="text-sm font-medium text-slate-700">Interest method</label>
          <select
            name="interest_method"
            value={form.interest_method}
            onChange={handleChange}
            className="mt-1 w-full rounded border border-slate-200 px-3 py-2"
          >
            <option value="simple">Simple (flat per period)</option>
            <option value="compound">Compound (day-count)</option>
          </select>
        </div>
      </div>

      <div className="flex gap-2">
        <button
          type="button"
          onClick={() => setStep(1)}
          className="rounded border border-slate-200 px-4 py-2 text-sm text-slate-600"
        >
          Back
        </button>
        <button
          type="button"
          onClick={handleSimulate}
          className="rounded bg-emerald-600 px-4 py-2 text-sm font-semibold text-white"
          disabled={loading}
        >
          {loading ? "Simulating..." : "Generate Preview"}
        </button>
      </div>
    </div>
  );

  const renderStep3 = () => (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold">Preview Schedule</h3>
      <p className="text-sm text-slate-600">
        Submitting creates a wallet withdrawal request for Accounts. The investment becomes active
        only after Accounts confirms and approves the withdrawal.
      </p>
      {summary ? (
        <div className="grid grid-cols-2 gap-3 rounded border border-slate-200 bg-slate-50 p-3 text-sm">
          <div>Total interest: {summary.total_interest?.toLocaleString()}</div>
          <div>Total cash in: {summary.total_cash_in?.toLocaleString()}</div>
          <div>Total cash out: {summary.total_cash_out?.toLocaleString()}</div>
          <div>Effective yield: {summary.effective_yield?.toFixed(2)}%</div>
        </div>
      ) : null}
      <div className="overflow-x-auto rounded border border-slate-200">
        <table className="min-w-full text-sm">
          <thead className="bg-slate-50">
            <tr>
              <th className="px-3 py-2 text-left">Period</th>
              <th className="px-3 py-2 text-left">Date</th>
              <th className="px-3 py-2 text-right">Opening</th>
              <th className="px-3 py-2 text-right">Interest</th>
              <th className="px-3 py-2 text-right">Principal</th>
              <th className="px-3 py-2 text-right">Closing</th>
              <th className="px-3 py-2">Maturity</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {schedule.map((row) => (
              <tr key={row.period_number}>
                <td className="px-3 py-2">{row.period_number}</td>
                <td className="px-3 py-2">{row.payment_date}</td>
                <td className="px-3 py-2 text-right">{Number(row.opening_principal).toLocaleString()}</td>
                <td className="px-3 py-2 text-right">{Number(row.interest_amount).toLocaleString()}</td>
                <td className="px-3 py-2 text-right">{Number(row.principal_repayment).toLocaleString()}</td>
                <td className="px-3 py-2 text-right">{Number(row.closing_principal).toLocaleString()}</td>
                <td className="px-3 py-2 text-center">{row.is_maturity ? "Yes" : ""}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="flex gap-2">
        <button
          type="button"
          onClick={() => setStep(2)}
          className="rounded border border-slate-200 px-4 py-2 text-sm text-slate-600"
        >
          Back
        </button>
        <button
          type="button"
          onClick={handleCreate}
          className="rounded bg-emerald-600 px-4 py-2 text-sm font-semibold text-white"
          disabled={loading}
        >
          {loading ? "Submitting..." : "Submit for Accounts approval"}
        </button>
      </div>
    </div>
  );

  const submittedRequests = requests.filter((item) => submittedRequestIds.includes(item.id));
  const submittedCompleted =
    submittedRequests.length > 0 && submittedRequests.every((item) => item.status === "active");
  const submittedRejected = submittedRequests.some((item) => item.status === "rejected");

  const renderStep4 = () => (
    <div className="space-y-4">
      <h3
        className={`text-lg font-semibold ${
          submittedRejected
            ? "text-red-700"
            : submittedCompleted
              ? "text-emerald-700"
              : "text-amber-700"
        }`}
      >
        {submittedRejected
          ? "Request rejected"
          : submittedCompleted
            ? "Fixed-income investment completed"
            : "Pending Accounts approval"}
      </h3>
      <p className="text-sm text-slate-700">
        {submittedCompleted
          ? "The withdrawal was approved and the fixed-income investment is now active."
          : success || "The withdrawal is awaiting Accounts review."}
      </p>
      {submittedRequests.map((item) => (
        <div key={item.id} className="rounded border border-slate-200 bg-slate-50 p-3 text-sm">
          <div>Investment request: #{item.id}</div>
          <div>Withdrawal: {item.withdrawal_status}</div>
          <div>Fixed income: {item.status}</div>
          {item.admin_note ? <div>Accounts note: {item.admin_note}</div> : null}
        </div>
      ))}
      <div className="flex gap-2">
        <button
          type="button"
          onClick={() => setStep(2)}
          className="rounded border border-slate-200 px-4 py-2 text-sm text-slate-600"
        >
          Create another
        </button>
      </div>
    </div>
  );

  return (
    <div className="space-y-4 rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
      <h2 className="text-2xl font-semibold text-slate-900">Fixed Income Wizard</h2>
      {error ? <p className="text-sm text-red-600">{error}</p> : null}
      {success && step === 4 ? <p className="text-sm text-emerald-700">{success}</p> : null}

      {step === 1 && renderStep1()}
      {step === 2 && renderStep2()}
      {step === 3 && renderStep3()}
      {step === 4 && renderStep4()}

      <div className="mt-8 border-t border-slate-200 pt-5">
        <h3 className="text-lg font-semibold text-slate-900">Recent fixed-income requests</h3>
        <div className="mt-3 overflow-x-auto rounded border border-slate-200">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-3 py-2 text-left">Client</th>
                <th className="px-3 py-2 text-left">Product</th>
                <th className="px-3 py-2 text-right">Principal</th>
                <th className="px-3 py-2 text-left">Withdrawal</th>
                <th className="px-3 py-2 text-left">Fixed income</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {requests.map((item) => (
                <tr key={item.id}>
                  <td className="px-3 py-2">{item.full_name || item.username}</td>
                  <td className="px-3 py-2">{item.product_type}</td>
                  <td className="px-3 py-2 text-right">
                    {Number(item.principal || 0).toLocaleString()}
                  </td>
                  <td className="px-3 py-2 capitalize">{item.withdrawal_status}</td>
                  <td className="px-3 py-2 capitalize">{item.status}</td>
                </tr>
              ))}
              {requests.length === 0 ? (
                <tr>
                  <td colSpan="5" className="px-3 py-6 text-center text-slate-500">
                    No fixed-income requests yet.
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default FixedIncomeWizard;
